import {
  Band,
  BRAND,
  brand,
  DownloadStrip,
  LogoStrip,
  Eyebrow,
  Do,
  Logo,
  Never,
  Page,
  PanelBar,
  Rule,
  Runner,
  SPACE_ACCENT,
  ui,
} from '../ui';
import { SITES, SITE_IDS, type SiteId } from '@goodbuild/rules/sites';
import { column } from './shared';
import { useDocumentTitle } from '../title';

/**
 * The brand, on a page of the product rather than in a file beside it.
 *
 * There is a `goodbuild-brand-guidelines.html` at the root of this repository
 * and it is the specification — every rule, every measurement, the argument for
 * each. This is not a replacement for it and does not try to be. It is the
 * shorter thing that a person who is not going to read a 40kB HTML document
 * still needs: what the mark is, what it means, what the colours are, and the
 * two or three rules that are actually load-bearing.
 *
 * **It is public.** A studio deciding whether to sign with us, a creator
 * deciding whether to put our code in a video, and anybody putting our logo in
 * a press post all need this, and none of them has an account. So it is not in
 * `APP_PATHS` and there is no gate on it.
 *
 * ## The one thing this page will not do
 *
 * **It does not draw an example `VersionStrip`.** Every other brand page in the
 * world shows its chart device filled with plausible numbers, and section 03's
 * single rule is that a strip must never draw a shape its data did not produce
 * — *"fabricating a rising staircase for a game that has not earned it turns
 * the platform's most honest device into its biggest lie, and it would be
 * noticed"*. A specimen on the brand page is exactly that lie, told on the page
 * that explains why we do not tell it.
 *
 * So the progress section shows `DownloadStrip`, which is equal bars and
 * implies no score, and says in words what the scored version looks like. If
 * somebody wants to see a real one, every store page has one.
 */
export function BrandRoute(): React.ReactElement {
  useDocumentTitle('Our logo');

  return (
    <Page site="store">
      <Runner left="Brand" right="GOODBUILD" />

      <header style={column(6)}>
        <Eyebrow>Brand</Eyebrow>
        <h1 className="gb-display">
          Five bars,
          <br />
          and what they mean
        </h1>
        <p className="gb-lead">
          Our mark is five bars. The same five bars, filled with real numbers, show whether a game
          got better. Use them, and please do not redraw them.
        </p>
      </header>

      <PanelBar />

      <Mark />
      <Rule />
      <Why />
      <Lockups />
      <Palette />
      <Progress />
      <Rules />
    </Page>
  );
}

/**
 * The mark itself, once large and then in each of the three accents.
 *
 * Three swatches rather than a paragraph saying it changes, because the fact
 * being shown is that it *is one mark* — same heights, same proportions, one
 * colour moving. A reader who has only ever seen the store's orange should be
 * able to see the studio's blue without going and signing up.
 *
 * `data-gb-space` on each swatch is the whole mechanism, not a demonstration of
 * it: that attribute is what resolves `--gb-accent` everywhere in this product,
 * and the mark takes it from its surroundings exactly as a chart or a button
 * does. Nothing here passes the mark a colour.
 */
function Mark(): React.ReactElement {
  return (
    <section style={column(6)}>
      <h2 className="gb-headline">The mark</h2>

      <div
        style={{
          display: 'flex',
          gap: 'var(--gb-space-8)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <LogoStrip size="lg" />

        <p className="gb-body" style={{ flex: '1 1 20rem', margin: 0 }}>
          Five rising bars at set heights &mdash; 30, 48, 66, 82, 100. The tallest takes the
          page&rsquo;s accent colour, the next two the type colour, and the last two a soft tint of
          the ground. That is the whole palette. The name beside it is always one solid colour.
        </p>
        <p className="gb-body" style={{ flex: '1 1 20rem', margin: 0 }}>
          The heights never change. They are the logo, not a measurement. The four-bar version below
          is the only other one allowed, for sixteen pixels and under, where the first two bars sit
          so close that only the last one reads.
        </p>
      </div>

      <div className={brand.g3 ?? ''}>
        {SITE_IDS.map((space) => (
          <Swatch key={space} space={space} />
        ))}
      </div>
    </section>
  );
}

function Swatch({ space }: { space: SiteId }): React.ReactElement {
  return (
    <div
      data-gb-space={space}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--gb-space-3)',
        padding: 'var(--gb-space-8) var(--gb-space-4)',
        border: '1px solid var(--gb-rule)',
      }}
    >
      <LogoStrip size="md" />
      <span className={ui.statLabel ?? ''}>{SITES[space].switcherLabel}</span>
      <span className={ui.numeric ?? ''} style={{ color: 'var(--gb-accent)' }}>
        {SPACE_ACCENT[space]}
      </span>
    </div>
  );
}

/**
 * Why the mark is a chart, in the three claims it actually makes.
 *
 * Written down on the page rather than only in the component, because a mark
 * whose meaning lives in a source comment is a mark that gets redrawn by
 * somebody who never read it. These are also three claims the product can be
 * held to, which is the point of choosing them.
 */
function Why(): React.ReactElement {
  return (
    <section style={column(6)}>
      <h2 className="gb-headline">Why a chart</h2>

      <div className={brand.g3 ?? ''}>
        <div style={column(2)}>
          <h3 className="gb-subhead">It is the shape of getting better</h3>
          <p className="gb-body">
            A game that really improved draws a staircase. That is the thing we care most about
            measuring, so it is the thing the logo should be &mdash; a picture of the claim, not an
            object standing in for the company.
          </p>
        </div>
        <div style={column(2)}>
          <h3 className="gb-subhead">The logo is the chart</h3>
          <p className="gb-body">
            Which is unusual, and is the point. Everywhere else those bars are real review scores,
            one per version. The logo is the same shape at set heights, so you learn to read the
            chart by looking at the logo.
          </p>
        </div>
        <div style={column(2)}>
          <h3 className="gb-subhead">We cannot fake it</h3>
          <p className="gb-body">
            The heights in the logo never move, and every other strip is worked out from numbers.
            Nothing in this product will draw bars you hand it. A logo built on a chart has to be
            one that cannot flatter us.
          </p>
        </div>
      </div>
    </section>
  );
}

/**
 * The two lockups, and the rule about the wordmark.
 *
 * `href={null}` on all of them: these are specimens, and a specimen that
 * navigates is a specimen somebody clicks by accident while reading. It is also
 * why the mark above is `title`-ed and these are not — the lockup already says
 * GOODBUILD in letters.
 */
function Lockups(): React.ReactElement {
  return (
    <section style={column(6)}>
      <h2 className="gb-headline">The lockup</h2>

      <div className={brand.g2 ?? ''}>
        <div style={column(4)}>
          <p className={ui.statLabel ?? ''}>Stacked — where there is room</p>
          <Logo size="2rem" href={null} />
        </div>
        <div style={column(4)}>
          <p className={ui.statLabel ?? ''}>Horizontal — navigation bars, headers</p>
          <Logo horizontal size="1.5rem" href={null} />
        </div>
      </div>

      <div className={brand.g3 ?? ''}>
        {SITE_IDS.map((space) => (
          <div key={space} data-gb-space={space} style={{ padding: 'var(--gb-space-4) 0' }}>
            <Logo horizontal size="1.25rem" site={space} href={null} />
          </div>
        ))}
      </div>

      <p className="gb-body">
        On the two work sites the name of the site sits beside our name, never inside it. Our name
        is Archivo at width 125, weight 900. Never restyle it, re-space it, outline it, or give it a
        colour of its own.
      </p>
    </section>
  );
}

/** The palette, as swatches that state their own hex. */
function Palette(): React.ReactElement {
  const swatches = [
    { name: 'Ink', hex: BRAND.ink, on: BRAND.paper, note: 'The ground almost everything sits on.' },
    { name: 'Paper', hex: BRAND.paper, on: BRAND.ink, note: 'Type on Ink, and the light ground.' },
    { name: 'Solar', hex: BRAND.solar, on: BRAND.ink, note: 'Players.' },
    { name: 'Hot', hex: BRAND.hot, on: BRAND.paper, note: 'Creators.' },
    { name: 'Build', hex: BRAND.build, on: BRAND.paper, note: 'The workshop.' },
    {
      name: 'Volt',
      hex: BRAND.volt,
      on: BRAND.ink,
      note: 'Live only. Under 5% of any layout, and it belongs to no panel.',
    },
  ];

  return (
    <section style={column(6)}>
      <h2 className="gb-headline">Colour</h2>

      <p className="gb-body">
        One accent colour per surface, so you know which part of the site you are on before reading
        a word. Flat colour and thin rules only. There are no gradients anywhere, and that is a rule
        rather than a taste.
      </p>

      <div className={brand.g3 ?? ''}>
        {swatches.map((swatch) => (
          <div
            key={swatch.name}
            style={{
              background: swatch.hex,
              color: swatch.on,
              padding: 'var(--gb-space-6)',
              border: '1px solid var(--gb-rule)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--gb-space-2)',
              minHeight: '9rem',
            }}
          >
            <strong style={{ fontSize: 'var(--gb-ui-label)', letterSpacing: '0.04em' }}>
              {swatch.name}
            </strong>
            <span className={ui.numeric ?? ''} style={{ fontSize: 'var(--gb-data-label)' }}>
              {swatch.hex.toUpperCase()}
            </span>
            <span style={{ fontSize: 'var(--gb-data-label)', opacity: 0.85 }}>{swatch.note}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * The signature device, and the reason there is no specimen of it here.
 *
 * The strip used to be the logo as well as the chart. It is only the chart now
 * — see the mark above — and the split makes the rule below easier to hold
 * rather than harder, because nothing about the device is decorative any more.
 */
function Progress(): React.ReactElement {
  return (
    <section style={column(6)}>
      <h2 className="gb-headline">The progress strip</h2>

      <p className="gb-body">
        The same five bars as the logo, filled with real numbers instead of set heights. Newest on
        the right, always in the page&rsquo;s accent colour. The heights are review scores, one per
        version. Real improvements draw a staircase, doing nothing draws a flat line, and a game
        that got worse draws a strip that falls. Falling is allowed, and required &mdash; the
        honesty is the whole device.
      </p>

      <div style={column(3)}>
        <p className={ui.statLabel ?? ''}>Progress state — equal bars, three of five complete</p>
        <DownloadStrip total={5} completed={3} size="lg" />
        <p className="gb-body">
          Its bars are equal because nothing here is a score.{' '}
          <strong>There is no example of the scored strip on purpose.</strong> Drawing one would
          mean making up review numbers for a game that does not exist, on the page explaining why
          we do not do that. Every game page has a real one.
        </p>
      </div>
    </section>
  );
}

/** The rules that outrank a preference. */
function Rules(): React.ReactElement {
  return (
    <>
      <Band tone="ink">
        <Eyebrow>Not negotiable</Eyebrow>
        <h2 className="gb-headline">Three rules</h2>

        <div className={brand.g2 ?? ''}>
          <Do title="Always">
            <ul>
              <li>Draw a strip from the numbers behind it, or do not draw one.</li>
              <li>Put the count beside every percentage.</li>
              <li>Give a page one accent colour, and let the mark take it.</li>
            </ul>
          </Do>
          <Never title="Never">
            <ul>
              <li>A gradient, a glow, or a drop shadow. Anywhere.</li>
              <li>A percentage on its own, however good it looks.</li>
              <li>A restyled, recoloured or redrawn name.</li>
            </ul>
          </Never>
        </div>
      </Band>

      <section style={column(4)}>
        <h2 className="gb-headline">Type</h2>
        <p className="gb-body">
          Archivo for words, at width 125 and weight 900 for the big ones. Martian Mono for every
          number, label and status, so a column of money lines up.
        </p>
      </section>
    </>
  );
}
